import { stringify } from 'query-string';
import { fetchUtils } from 'ra-core';

const { DEBUG } = process.env;

export default (
    apiUrl,
    httpClient = fetchUtils.fetchJson,
    countHeader = 'Content-Range'
) => ({
    getList: (resource, params) => {
        // console.log(`calling getList on ${resource} with params: %o`, params)
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;

        const rangeStart = (page - 1) * perPage;
        const rangeEnd = perPage;

        const query = {
            sort: JSON.stringify([field, order]),
            range: JSON.stringify([rangeStart, rangeEnd]),
            filter: JSON.stringify(params.filter),
        };
        const url = `${apiUrl}/${resource}?${stringify(query)}`;
        const options =
            countHeader === 'Content-Range'
                ? {
                      // Chrome doesn't return `Content-Range` header if no `Range` is provided in the request.
                      headers: new Headers({
                          Range: `${resource}=${rangeStart}-${rangeEnd}`,
                      }),
                  }
                : {};

        return httpClient(url, options).then((response) => {
            const {data, total} = extractResponseData(response, countHeader);

            if (typeof total === 'undefined' && !response.headers.has(countHeader)) {
                throw new Error(
                    `The ${countHeader} header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare ${countHeader} in the Access-Control-Expose-Headers header?`
                );
            }
            return { data, total }
        });
    },

    getOne: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/${params.id}`).then(({json}) => ({
            data: convertJSONToData(json),
        })),

    getMany: (resource, params) => {
        DEBUG && console.log(`calling getMany on ${resource} with params: %o`, params)
        const filterBy = params.meta?.fieldName || 'id'

        const query = {
            filter: JSON.stringify({ [filterBy]: params.ids }),
        };
        const url = `${apiUrl}/${resource}?${stringify(query)}`;
        return httpClient(url).then(( response) => {
            return extractResponseData(response, 'content-range', filterBy);
        });
    },

    getManyReference: (resource, params) => {
        DEBUG && console.log(`calling getManyReference on ${resource} with params: %o`, params)
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;

        const rangeStart = (page - 1) * perPage;
        const rangeEnd = perPage;

        const query = {
            sort: JSON.stringify([field, order]),
            range: JSON.stringify([(page - 1) * perPage, perPage]),
            filter: JSON.stringify({
                ...params.filter,
                [params.target]: params.id,
            }),
        };
        const url = `${apiUrl}/${resource}?${stringify(query)}`;
        const options =
            countHeader === 'Content-Range'
                ? {
                      // Chrome doesn't return `Content-Range` header if no `Range` is provided in the request.
                      headers: new Headers({
                          Range: `${resource}=${rangeStart}-${rangeEnd}`,
                      }),
                  }
                : {};

        return httpClient(url, options).then((response) => {
                const {data, total} = extractResponseData(response, countHeader);

            if (typeof total === 'undefined' && !response.headers.has(countHeader)) {
                throw new Error(
                    `The ${countHeader} header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare ${countHeader} in the Access-Control-Expose-Headers header?`
                );
            }
            return { data, total }
        });
    },

    update: (resource, params) => {
        const endpoint = `${apiUrl}/${resource}/${params.id}`;

        return httpClient(endpoint, {
            method: 'PUT',
            body: JSON.stringify(params.data),
        }).then(({json}) => {
            return convertJSONToData(json)
        }).catch(err => {
            console.log(err, resource)
            throw err;
        });
    },
    // simple-rest doesn't handle provide an updateMany route, so we fall back to calling update n times instead
    updateMany: (resource, params) =>
        Promise.all(
            params.ids.map(id =>
                httpClient(`${apiUrl}/${resource}/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(params.data),
                })
            )
        ).then(responses => ({ data: responses.map(({ json }) => json.id) })),

    create: (resource, params) =>
        httpClient(`${apiUrl}/${resource}`, {
            method: 'POST',
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({
            data: { ...params.data, id: json.id },
        })),

    delete: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/${params.id}`, {
            method: 'DELETE',
            headers: new Headers({
                'Content-Type': 'text/plain',
            }),
        }).then(({ json }) => ({ data: json })),

    // simple-rest doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
    deleteMany: (resource, params) =>
        Promise.all(
            params.ids.map(id =>
                httpClient(`${apiUrl}/${resource}/${id}`, {
                    method: 'DELETE',
                    headers: new Headers({
                        'Content-Type': 'text/plain',
                    }),
                })
            )
        ).then(responses => ({
            data: responses.map(({ json }) => json.id),
        })),
});

const extractResponseData = (response, countHeader = 'content-range', idFieldName = 'id') => {
    const extracted = convertJSONToData(response.json, idFieldName);
    const {data} = extracted;
    let {total} = extracted;
    if (typeof total === 'undefined') {
        const rangeHeaderLabel = response.headers.has(countHeader)
            ? response.headers.get(countHeader)
            : response.headers.has(countHeader.toLowerCase())
                ? response.headers.get(countHeader.toLowerCase())
                : null;
        if (rangeHeaderLabel) {
            const headerTotal = parseInt(rangeHeaderLabel.split('/').pop(), 10)
            if (headerTotal) total = headerTotal;
        }
    }
    if (!total) total = data.length;
    return {data, total}
}

const convertJSONToData = (json, idFieldName = 'id') => {
    if (Array.isArray(json)){
        return {data: json};
    }
    const keys = Object.keys(json);
    if (keys.length === 1){
        return {data:json[keys[0]]}
    }
    if (keys.length === 2 && typeof json.total != "undefined"){
        return {data:json[keys[0]], total: json.total}
    }
    if (!json.data) {
        json.data = [];
        json.total = 0;
    }
    return json;
}
const reKeyData = (data, idFieldName = 'id') => {
    // no re-key necessary
    if (idFieldName === 'id') return data;

}